import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Select, { components } from "react-select";

import { KEYBOARD_KEYS } from "../../constants/global.constants";

import { isNullOrUndefined, combineClassNames } from "../../utils/appUtils";

import WPInputHelperText from "../WPInputHelperText";
import WPInputLabel from "../WPInputLabel";

import AccordionDownArrowIcon from "../../common/icons/AccordionDownArrowIcon";
import AccordionUpArrowIcon from "../../common/icons/AccordionUpArrowIcon";
import CancelIcon from "../../assets/svgIcons/Cancel";
import CircularLoader from "../../common/styles/components/circularLoader";
import CustomTooltip from "../../common/styles/components/tooltip/CustomTooltip";

import "./WPDropdown.scss";

const CustomDropdownIndicator = (props) => {
  const { value, isMulti, clearValue, showCancelIcon } = props.selectProps;
  // Show cancel icon only if showCancelIcon prop is true, value is present, and not multi-select
  if (
    showCancelIcon &&
    !isMulti &&
    value &&
    (Array.isArray(value)
      ? value.length > 0
      : value !== null && value !== undefined && value !== "")
  ) {
    return (
      <div
        className="dropdown-cancel-icon"
        onClick={(e) => {
          e.stopPropagation();
          clearValue();
        }}
      >
        <CancelIcon />
      </div>
    );
  }
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        {props.selectProps.menuIsOpen ? (
          <AccordionUpArrowIcon />
        ) : (
          <AccordionDownArrowIcon />
        )}
      </components.DropdownIndicator>
    )
  );
};

const CustomOption = ({ children, ...props }) => (
  <div
    id={`dropdonw-option-${props.data.value}`}
    className={props.isDisabled ? "disabled-option" : ""}
  >
    <components.Option {...props}>{children}</components.Option>
  </div>
);

const LoadingIndicator = () => <CircularLoader size="20px" />;

const WPDropdown = forwardRef((props, ref) => {
  const {
    id,
    name,
    value,
    label,
    options,
    defaultValue,
    getOptionLabel,
    getOptionValue,
    onChange,
    isMulti,
    disabled,
    tabIndex,
    isLoading,
    menuPosition = "fixed",
    isOptionDisabled,
    formatGroupLabel,
    placeholder = "",
    isError,
    helperText,
    required,
    showCancelIcon,
    customClassName,
  } = props;
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [overflowTitle, setOverflowTitle] = useState("");
  const containerRef = useRef(null);

  const handleChange = (selectedOption) => {
    onChange(selectedOption, name);
  };

  const defaultFormatGroupLabel = (data) => (
    <div className="select__grouping">
      <span>{data.label}</span>
    </div>
  );

  // Support passing just the identifier value (primary key) as value
  const optionValueGetter = useMemo(
    () => getOptionValue || ((opt) => opt.value),
    [getOptionValue],
  );

  const findOption = (val) =>
    options?.find((opt) => optionValueGetter?.(opt) === val) || val;

  // Support both full option objects and raw values (e.g., primary key)
  const optionValue = useMemo(() => {
    if (isNullOrUndefined(value)) return value;
    if (Array.isArray(value)) {
      return value.map((v) => (typeof v === "object" ? v : findOption(v)));
    }
    return typeof value === "object" ? value : findOption(value);
  }, [value, options, optionValueGetter]);

  const selectedDisplayLabel = useMemo(() => {
    if (isNullOrUndefined(optionValue) || Array.isArray(optionValue)) {
      return "";
    }

    const selectedOption =
      typeof optionValue === "object" ? optionValue : findOption(optionValue);

    if (isNullOrUndefined(selectedOption)) {
      return "";
    }

    if (getOptionLabel) {
      const customLabel = getOptionLabel(selectedOption);
      return typeof customLabel === "string"
        ? customLabel
        : `${customLabel || ""}`;
    }

    return selectedOption?.label || `${selectedOption?.value || ""}`;
  }, [optionValue, findOption, getOptionLabel]);

  useEffect(() => {
    const checkOverflow = () => {
      const singleValueElement = containerRef.current?.querySelector(
        ".select__single-value",
      );

      if (!singleValueElement || !selectedDisplayLabel) {
        setOverflowTitle("");
        return;
      }

      const isOverflowing =
        (singleValueElement.scrollWidth || 0) >
        (singleValueElement.clientWidth || 0);

      setOverflowTitle(isOverflowing ? selectedDisplayLabel : "");
    };

    const rafId = window.requestAnimationFrame(checkOverflow);
    window.addEventListener("resize", checkOverflow);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [selectedDisplayLabel, menuIsOpen]);

  const handleKeyDown = useCallback((event) => {
    const { key } = event;
    if (key === KEYBOARD_KEYS.ARROWUP || key === KEYBOARD_KEYS.ARROWDOWN) {
      event.stopPropagation();
    }
  }, []);

  return (
    <div className={combineClassNames("dropdown-container", customClassName)}>
      {label && (
        <WPInputLabel label={label} disabled={disabled} required={required} />
      )}
      <CustomTooltip text={overflowTitle} placement="bottom">
        <div ref={containerRef} className="wp-dropdown-tooltip-trigger">
          <Select
            ref={ref} // Pass the ref to the Select component
            id={id || "dropdown_id"}
            name={name || id || "dropdown_name"}
            defaultValue={defaultValue}
            value={optionValue ?? ""}
            isDisabled={disabled || isLoading}
            isMulti={isMulti}
            closeMenuOnSelect={!isMulti}
            onChange={handleChange}
            options={options}
            className={`basic-multi-select${isError ? " dropdown-error" : ""}`}
            classNamePrefix="select"
            placeholder={placeholder}
            inputId={id + "_input" || "dropdown_input"}
            components={{
              DropdownIndicator: CustomDropdownIndicator,
              Option: CustomOption,
              LoadingIndicator,
            }}
            onMenuOpen={() => setMenuIsOpen(true)}
            menuPosition={menuPosition}
            onMenuClose={() => setMenuIsOpen(false)}
            menuIsOpen={menuIsOpen}
            formatGroupLabel={formatGroupLabel || defaultFormatGroupLabel}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            tabIndex={tabIndex}
            isOptionDisabled={isOptionDisabled}
            isLoading={isLoading}
            onKeyDown={handleKeyDown}
            styles={{
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
              }),
            }}
            menuPortalTarget={document.body}
            clearValue={() => handleChange(null)}
            showCancelIcon={showCancelIcon}
          />
        </div>
      </CustomTooltip>
      {isError && (
        <WPInputHelperText
          position="static"
          isError={isError}
          text={helperText}
        />
      )}
    </div>
  );
});

WPDropdown.displayName = "WPDropdown";

export default WPDropdown;
